import type { MusicVideoItemData } from "@/home/work/MusicVideoItem";
import { Logger } from "@/utils/Logger";

/** 글로벌 GSAP (레이아웃에서 로드) */
type GsapLite = {
  to: (
    targets: Element | Element[] | NodeListOf<Element>,
    vars: Record<string, unknown>,
  ) => unknown;
};

interface WorksPayload {
  works: Record<string, MusicVideoItemData[]>;
  defaultId: number;
}

function parseWorksJson(): WorksPayload | null {
  const el = document.getElementById("dir-page-works-json");
  if (!el?.textContent?.trim()) return null;
  try {
    return JSON.parse(el.textContent) as WorksPayload;
  } catch (e) {
    Logger.error("DirectorsPage: invalid JSON", e);
    return null;
  }
}

function applyDirectorsBackdrop(bgEl: HTMLElement): void {
  bgEl.style.backgroundImage = "";
  bgEl.style.backgroundColor = "#000";
  bgEl.style.opacity = "";
  bgEl.classList.remove("is-visible");
}

function firstYoutubeId(list: MusicVideoItemData[]): string | null {
  for (const w of list) {
    const id = w.youtubeId?.trim();
    if (id) return id;
  }
  return null;
}

function buildEmbedSrc(videoId: string): string {
  const p = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    controls: "0",
    loop: "1",
    playlist: videoId,
  });
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${p.toString()}`;
}

/**
 * PC: 이름 호버 시 배경에 대표(첫 번째) 뮤직비디오 미리보기 재생. 모바일: Global/Korea 탭만.
 */
export class DirectorsPage {
  private readonly root: HTMLElement;
  private readonly bgEl: HTMLElement;
  private readonly videoLayer: HTMLElement | null;
  private readonly videoFrame: HTMLIFrameElement | null;
  private readonly desktop: HTMLElement | null;
  private payload: WorksPayload | null;
  private activeId = 0;
  private mq: MediaQueryList;
  private clearPreviewTimer: number | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.bgEl = root.querySelector<HTMLElement>("[data-dir-bg]")!;
    this.videoLayer = root.querySelector<HTMLElement>("[data-dir-video-layer]");
    this.videoFrame = root.querySelector<HTMLIFrameElement>("[data-dir-video-frame]");
    this.desktop = root.querySelector<HTMLElement>("[data-dir-desktop]");
    this.payload = parseWorksJson();
    this.mq = window.matchMedia("(min-width: 1024px)");
  }

  init(): void {
    this.initMobileTabs();
    applyDirectorsBackdrop(this.bgEl);
    this.hideBackdropVideo();
    if (!this.payload || !this.desktop) return;

    const apply = (): void => {
      if (this.mq.matches) {
        this.bindDesktop();
      } else {
        this.unbindDesktop();
        this.hideBackdropVideo();
        this.clearActiveStyles();
      }
    };
    apply();
    this.mq.addEventListener("change", apply);
  }

  private desktopBound = false;

  private readonly onEnter = (e: Event) => {
    if (this.clearPreviewTimer !== null) {
      window.clearTimeout(this.clearPreviewTimer);
      this.clearPreviewTimer = null;
    }
    const t = e.currentTarget as HTMLElement;
    const a = t.closest("a.dir-page__name") ?? t;
    const id = parseInt(a.getAttribute("data-dir-id") ?? "0", 10);
    if (id > 0) this.selectDirector(id);
  };

  private readonly onFocus = (e: Event) => {
    this.onEnter(e);
  };

  private readonly onLeave = (): void => {
    if (this.clearPreviewTimer !== null) {
      window.clearTimeout(this.clearPreviewTimer);
    }
    this.clearPreviewTimer = window.setTimeout(() => {
      this.clearPreviewTimer = null;
      this.hideBackdropVideo();
      this.clearActiveStyles();
      this.activeId = 0;
    }, 120);
  };

  private bindDesktop(): void {
    if (this.desktopBound) return;
    this.desktopBound = true;
    this.desktop!.querySelectorAll("a.dir-page__name").forEach((a) => {
      a.addEventListener("mouseenter", this.onEnter);
      a.addEventListener("mouseleave", this.onLeave);
      a.addEventListener("focus", this.onFocus);
      a.addEventListener("blur", this.onLeave);
    });
  }

  private unbindDesktop(): void {
    if (!this.desktopBound) return;
    this.desktopBound = false;
    this.desktop!.querySelectorAll("a.dir-page__name").forEach((a) => {
      a.removeEventListener("mouseenter", this.onEnter);
      a.removeEventListener("mouseleave", this.onLeave);
      a.removeEventListener("focus", this.onFocus);
      a.removeEventListener("blur", this.onLeave);
    });
  }

  private clearActiveStyles(): void {
    if (!this.desktop) return;
    this.desktop.querySelectorAll("a.dir-page__name").forEach((a) => {
      a.classList.remove("is-active");
    });
    this.desktop.querySelectorAll<HTMLElement>(".dir-page__name-underline").forEach((el) => {
      el.style.transform = "scaleX(0)";
    });
  }

  private selectDirector(id: number): void {
    if (!this.payload || !this.desktop) return;
    const gsap = (window as unknown as { gsap?: GsapLite }).gsap;

    this.activeId = id;
    const list = this.payload.works[String(id)] ?? [];
    const ytId = firstYoutubeId(list);

    const allLinks =
      this.desktop.querySelectorAll<HTMLAnchorElement>("a.dir-page__name");
    const activeLink = this.desktop.querySelector<HTMLAnchorElement>(
      `a.dir-page__name[data-dir-id="${id}"]`,
    );
    const activeUnderbar = activeLink?.querySelector<HTMLElement>(
      ".dir-page__name-underline",
    );
    const allUnderbars = this.desktop.querySelectorAll<HTMLElement>(
      ".dir-page__name-underline",
    );

    const updateUi = (): void => {
      allLinks.forEach((a) => {
        const aid = parseInt(a.getAttribute("data-dir-id") ?? "0", 10);
        a.classList.toggle("is-active", aid === id);
      });
      applyDirectorsBackdrop(this.bgEl);
      if (ytId && this.videoFrame && this.videoLayer) {
        this.videoFrame.title =
          activeLink
            ?.querySelector(".dir-page__name-inner")
            ?.textContent?.trim() || "Preview";
        this.videoFrame.src = buildEmbedSrc(ytId);
        this.videoLayer.classList.add("is-active");
      } else {
        this.hideBackdropVideo();
      }
    };

    if (!gsap) {
      updateUi();
      allUnderbars.forEach((el) => {
        el.style.transform =
          el === activeUnderbar ? "scaleX(1)" : "scaleX(0)";
      });
      return;
    }

    allUnderbars.forEach((el) => {
      gsap.to(el, {
        scaleX: el === activeUnderbar ? 1 : 0,
        duration: el === activeUnderbar ? 0.28 : 0.2,
        ease: "power2.out",
      });
    });
    updateUi();
  }

  private hideBackdropVideo(): void {
    if (this.videoFrame) {
      this.videoFrame.src = "";
      this.videoFrame.removeAttribute("src");
    }
    this.videoLayer?.classList.remove("is-active");
  }

  private initMobileTabs(): void {
    const mobile = this.root.querySelector<HTMLElement>("[data-dir-mobile]");
    if (!mobile) return;

    const tabs = mobile.querySelectorAll<HTMLButtonElement>("[data-dir-tab]");
    const panels = mobile.querySelectorAll<HTMLElement>("[data-dir-panel]");

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const key = tab.getAttribute("data-dir-tab");
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle("is-active", on);
          t.setAttribute("aria-selected", on ? "true" : "false");
        });
        panels.forEach((p) => {
          const show = p.getAttribute("data-dir-panel") === key;
          p.classList.toggle("is-active", show);
          p.hidden = !show;
        });
      });
    });
  }
}

export function initDirectorsPage(): void {
  const root = document.querySelector<HTMLElement>("[data-dir-page]");
  if (!root) return;
  try {
    new DirectorsPage(root).init();
  } catch (e) {
    Logger.error("initDirectorsPage", e);
  }
}
